"use client"

import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react"

/**
 * The phone-side shell behaviour shared by the owner and barber notification
 * panels: the sheet drops from the top, and a grip on its bottom edge lets
 * the reader pull it taller (down to the home indicator) or shorter.
 *
 * Height is state in px on the phone and `undefined` at sm+, where the panel
 * is a full-height side tray and keeps its Tailwind sizing. It opens nearly
 * full height, 24px short of the bottom: it used to stop 8rem short to leave
 * the launcher reachable below it, which made the list feel cut off; the
 * launcher now steps aside while the sheet is open (hooks/use-obscured-by-zone).
 */
const SM = 640
const DEFAULT_GAP = 24 // the same 24px reveal as the tallest drag
const MIN_HEIGHT = 260

export function useNotificationSheet(isOpen: boolean) {
  const [height, setHeight] = useState<number | undefined>(undefined)
  const dragging = useRef<{ startY: number; startH: number } | null>(null)

  // Tallest the sheet goes: a 24px reveal of the page above the home indicator.
  const maxHeight = useCallback(() => window.innerHeight - 24, [])

  // Size on open (and re-size when the phone rotates); clear at sm+.
  useEffect(() => {
    if (!isOpen) return
    const apply = () => {
      if (window.innerWidth >= SM) {
        setHeight(undefined)
        return
      }
      setHeight((h) => Math.min(h ?? window.innerHeight - DEFAULT_GAP, maxHeight()))
    }
    apply()
    window.addEventListener("resize", apply)
    return () => window.removeEventListener("resize", apply)
  }, [isOpen, maxHeight])

  const onGripPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (height === undefined) return
      dragging.current = { startY: e.clientY, startH: height }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [height],
  )
  const onGripPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const d = dragging.current
      if (!d) return
      const next = Math.max(MIN_HEIGHT, Math.min(maxHeight(), d.startH + (e.clientY - d.startY)))
      setHeight(next)
    },
    [maxHeight],
  )
  const onGripPointerUp = useCallback(() => {
    dragging.current = null
  }, [])

  return { height, gripProps: { onPointerDown: onGripPointerDown, onPointerMove: onGripPointerMove, onPointerUp: onGripPointerUp, onPointerCancel: onGripPointerUp } }
}

/** The grip row at the sheet's bottom edge — phone only. */
export function NotificationSheetGrip(props: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      {...props}
      role="separator"
      aria-label="Drag to resize"
      className="sm:hidden flex-shrink-0 flex items-center justify-center h-7 touch-none cursor-ns-resize select-none"
    >
      <span className="h-1.5 w-12 rounded-full bg-muted-foreground/30" />
    </div>
  )
}
